import React, { useState, useRef, useEffect, memo } from 'react';
import * as THREE from 'three';
import { TubeCurve } from '../shape/Spline';
import { Point } from "../shape/Point";
//import { Geometry, Base, Subtraction, Addition } from '@react-three/csg'
import { SUBTRACTION, Brush, Evaluator } from 'three-bvh-csg';
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import { drawIntersectionPoints,getContours } from './utils';
import {
  offsetCurve, 
  offsetCurveMm, 
  sortPoints, 
  calculateCurveCenter, 
  calculateIntersectionPoints, 
  calcCentroid,
  getBottomPoints,
  alignSeamPoints,
  makeCurveClockwise,
  createLoftSurfaceFrom2Curves,
  createLoftSurfaceFrom2CurvesSmooth,
  createLoftSurfaceFromCurves,
  getContourPointsCircumAll,
  offsetPointsNormalBufferGeometry,
  calcWrapPointsLowerUpperSurface,
  mergeLowerUpperPoints,
  createBottmCapSurface,
  calculateTweenCurve,
  calculateCurveLength,
  smoothGeom,
  findClosestPointOnPoints,
} from '../shape/utils';
import { BorderHorizontal } from '@material-ui/icons';


/**
 * Applies top edge reinforcement to a mesh.
 *
 * @param {THREE.Mesh} mesh - The mesh to apply the reinforcement to.
 * @param {number} reinforcementWidth - The width of the reinforcement.
 * @param {number} reinforcementThickness - The thickness of the reinforcement.
 * @param {number[]} distances - The distances for the bezier curve.
 * @param {number[]} targetIdxs - The indices of the target points.
 * @returns {THREE.Mesh} The mesh with the applied top edge reinforcement.
 */
const applyTopEdgeReinforcement = (mesh, reinforcementWidth, reinforcementThickness, distances, targetIdxs) => {
  // https://www.geogebra.org/m/pnmts2xu
  let A = new THREE.Vector2(0, 0);
  let B = new THREE.Vector2(0, 1.33);
  let C = new THREE.Vector2(1, 1.33);
  let D = new THREE.Vector2(1, 0);

  const bezierCurve = new THREE.CubicBezierCurve(A, B, C, D);

  // around top edge
  for (let i = 0; i < targetIdxs.length; i++) {
    const idx = targetIdxs[i];
    const ratio = reinforcementThickness * bezierCurve.getPoint(distances[i] / reinforcementWidth).y;
    // move point
    mesh.geometry.attributes.position.array[idx] += mesh.geometry.attributes.normal.array[idx] * ratio;
    mesh.geometry.attributes.position.array[idx+1] += mesh.geometry.attributes.normal.array[idx+1] * ratio;
    mesh.geometry.attributes.position.array[idx+2] += mesh.geometry.attributes.normal.array[idx+2] * ratio;
  }

  return mesh
}

/**
 * Applies weaknest area reinforcement to a mesh.
 * 
 * @param {THREE.Mesh} mesh - The mesh to apply reinforcement to.
 * @param {number} reinforcementWidth - The width of the reinforcement.
 * @param {number} reinforcementThickness - The thickness of the reinforcement.
 * @param {number} refY - The reference Y coordinate.
 * @param {number[]} targetIdxs - The indices of the target vertices.
 * @param {number[]} reinforcementVerticesIdx - The indices of the reinforcement vertices.
 * @param {number} filletArea - The fillet area.
 * @returns {THREE.Mesh} The mesh with applied reinforcement.
 */
const applyWeaknestAreaReinforcement = (
  mesh, 
  reinforcementWidth, 
  reinforcementThickness, 
  refY, 
  targetIdxs,
  topEdgeReinforcementVerticesIdx,
  vertices,
  filletArea,
  distances
  ) => {
  let A = new THREE.Vector2(0, 0);
  let B = new THREE.Vector2(0.32, 0);
  let C = new THREE.Vector2(0.68, 1);
  let D = new THREE.Vector2(1, 1);

  const bezierCurve = new THREE.CubicBezierCurve(A, B, C, D);

  let ratioTopEdge = 0;
  const maxDistance = distances.reduce((a, b) => Math.max(a, b));

  const angle = Math.PI*50/180;
  // around weaknest area
  for (let i = 0; i < targetIdxs.length; i++) {
    const idx = targetIdxs[i];
    
    // Temporary implemantation to smooth the reinforcement around top edge
    const topEdgeIdx = topEdgeReinforcementVerticesIdx.indexOf(idx);
    if (topEdgeIdx !== -1) {
      ratioTopEdge = distances[topEdgeIdx] / maxDistance;
    } else {
      ratioTopEdge = 1;
    };
    
    // skip around PTB area by angle, as tentatively
    const vertex = new THREE.Vector2(vertices[idx], vertices[idx+2]);
    const vector0 = new THREE.Vector2(0, 1);
    const vector1 = new THREE.Vector2(vertex.x, vertex.y);
    const angle0 = vector0.angleTo(vector1);
    let ratioAngle = 1;
    if (angle0 > -angle && angle0 < angle) {
      //continue;
      ratioAngle = Math.abs(angle0) / angle;
    }
    
    const distance = Math.abs(refY - mesh.geometry.attributes.position.array[idx+1]);
    
    let ratioDistance = 1;
    if (distance < reinforcementWidth/2+filletArea) {
      ratioDistance = (1 - bezierCurve.getPoint(Math.abs(distance / (reinforcementWidth/2+filletArea))).y);
    } else {
      continue
    }

    const ratio = reinforcementThickness * ratioAngle * ratioDistance * ratioTopEdge;
    // move point
    mesh.geometry.attributes.position.array[idx] += mesh.geometry.attributes.normal.array[idx] * ratio;
    mesh.geometry.attributes.position.array[idx+1] += mesh.geometry.attributes.normal.array[idx+1] * ratio;
    mesh.geometry.attributes.position.array[idx+2] += mesh.geometry.attributes.normal.array[idx+2] * ratio;
  }

  return mesh
}



const MOutputBkFd = memo(({
  oriMeshSocket,
  oriInnerUpperEdgePoints,
  oriOuterUpperEdgePoints,
  thickness,
  step,
  reinforcementTopEdgeThickness,
  reinforcementTopEdgeWidth,
  reinforcementWeaknestAreaThickness,
  reinforcementWeaknestAreaWidth,
  callback0,
  color,
  colorHigh,
  colorSub
}) => {
  console.log('mOutputBkFd');

  const objects = [];
  const objectsSub = [];
  var key = 0;

  const filletArea = 10;

  const material = new THREE.MeshLambertMaterial( { color: color, side: THREE.DoubleSide, transparent: true, opacity: 0.5} )
  const materialHigh = new THREE.MeshLambertMaterial( { color: colorHigh, side: THREE.DoubleSide, transparent: true, opacity: 0.5} )

  const meshSocket = oriMeshSocket.clone(); // non-index geometry
  meshSocket.geometry = oriMeshSocket.geometry.clone(); // for deepcopy

  meshSocket.geometry = BufferGeometryUtils.mergeVertices(meshSocket.geometry) // convert to indexed geometry
  meshSocket.geometry.computeBoundingBox();
  meshSocket.geometry.computeVertexNormals();

  reinforcementTopEdgeWidth = Number(reinforcementTopEdgeWidth) + 3; // 3 is for fillet

  const outerUpperEdgePoints = new THREE.CatmullRomCurve3(oriOuterUpperEdgePoints).getSpacedPoints(500);
  const innerUpperEdgePoints = new THREE.CatmullRomCurve3(oriInnerUpperEdgePoints).getSpacedPoints(100);


  // objectsSub.push(<TubeCurve 
  //   key={key} 
  //   vpoints={outerUpperEdgePoints} 
  //   radius={0.5}
  //   color={colorHigh}
  //   />
  // );
  // key += 1;

  // objectsSub.push(<TubeCurve 
  //   key={key} 
  //   vpoints={innerUpperEdgePoints} 
  //   radius={0.5}
  //   color={colorHigh}
  //   />
  // );
  // key += 1;

  // find vertices of meshSocket which the distance from outerUpperEdgePoints is reinforcementTopEdgeWidth
  // and offset the vertices as normal direction
  const vertices = meshSocket.geometry.attributes.position.array;
  const ignoreVerticesIdx = [];
  const normals = meshSocket.geometry.attributes.normal.array;
  const reinforcementVerticesIdx = [];
  const distances = [];
  for (let i = 0; i < vertices.length; i+=3) {
    if (i % 10000 === 0) console.log(i, vertices.length);
    const vertex = new THREE.Vector3(vertices[i], vertices[i+1], vertices[i+2]);
    // if (vertex.y < -30) {
    //   //ignoreVerticesIdx.push(i);
    //   continue; // to speed up
    // }
    
    // skip upper surface
    const closestPointToInnerUpperEdge = findClosestPointOnPoints(innerUpperEdgePoints, vertex);
    if (vertex.distanceTo(closestPointToInnerUpperEdge.point) < thickness) {
      ignoreVerticesIdx.push(i);
      continue;
    }

    // judge outer surface or not by distance from origin
    const origin = new THREE.Vector3(0, 0, 0);
    const offsetVertex = new THREE.Vector3(vertex.x + normals[i], vertex.y + normals[i+1], vertex.z + normals[i+2]);
    const dist0 = vertex.distanceTo(origin);
    const dist1 = offsetVertex.distanceTo(origin);
    if (dist0 > dist1) {
      ignoreVerticesIdx.push(i);
      continue;
    }
    // even though the above filter, there are some vertices which are not on the outer surface, like between outer and inner surface


    // find target vertices
    const closestPoint = findClosestPointOnPoints(outerUpperEdgePoints, vertex);
    const distance = vertex.distanceTo(closestPoint.point);
    if ( Math.abs(distance) < (reinforcementTopEdgeWidth)) {
      reinforcementVerticesIdx.push(i);
      distances.push(distance);
    }
  }

  // find verticies of meshSocket for band type reinforcement around weaknest area
  const reinforcementWeaknestVerticesIdx = [];
  // find the lowest point of outerUpperEdgePoints
  let refY = 9999;
  for (let i = 0; i < outerUpperEdgePoints.length; i++) {
    if (outerUpperEdgePoints[i].y < refY) {
      refY = outerUpperEdgePoints[i].y;
    }
  }
  for (let i = 0; i < vertices.length; i+=3) {
    const vertex = new THREE.Vector3(vertices[i], vertices[i+1], vertices[i+2]);
    
    if (ignoreVerticesIdx.includes(i)) continue; // to speed up
    if (Math.abs(vertex.y) > reinforcementWeaknestAreaWidth/2+filletArea) {
      continue; // to speed up
    }
    // find target vertices
    if ( vertex.y < refY+reinforcementWeaknestAreaWidth/2+filletArea && vertex.y > refY-(reinforcementWeaknestAreaWidth/2+filletArea) ) {
      reinforcementWeaknestVerticesIdx.push(i);
    }
  }

  // for (let i = 0; i < reinforcementWeaknestVerticesIdx.length; i++) {
  //   const vertex = new THREE.Vector3(vertices[reinforcementWeaknestVerticesIdx[i]], vertices[reinforcementWeaknestVerticesIdx[i]+1], vertices[reinforcementWeaknestVerticesIdx[i]+2]);
  //   objectsSub.push(<Point key={key} vpoint={vertex} color={colorHigh} size={2}/>);
  //   key += 1;
  // }

  if ( step == 1 ) {
    // offset mesh vertices as normal directiond
    meshSocket.geometry.attributes.position.needsUpdate = true;
    meshSocket.geometry.attributes.normal.needsUpdate = true;
    meshSocket.geometry.index.needsUpdate = true;
    meshSocket.geometry.computeVertexNormals();

    applyWeaknestAreaReinforcement(meshSocket, reinforcementWeaknestAreaWidth, reinforcementWeaknestAreaThickness, refY, reinforcementWeaknestVerticesIdx, reinforcementVerticesIdx, vertices, filletArea, distances);
    applyTopEdgeReinforcement(meshSocket, reinforcementTopEdgeWidth, reinforcementTopEdgeThickness, distances, reinforcementVerticesIdx);

    let geom = meshSocket.geometry;
    geom = BufferGeometryUtils.mergeVertices(geom, 1e-1);
    geom.computeVertexNormals();

    //geom = smoothGeom(geom, 2);

    const meshSocketSmooth = new THREE.Mesh(geom, material);

    callback0(meshSocketSmooth);
    //callback0(meshSocket)
  }


  return (
    (objectsSub.length > 0) ? (
      <group >
        {objectsSub}
      </group>
    ) : null
  )
});

export default MOutputBkFd
